import React, { useState, useCallback } from "react";
import { Button, Card, Tag, Spin, Typography, Empty, message } from "antd";
import { SearchOutlined, SwapOutlined } from "@ant-design/icons";

import AirportSelector from "../../../../components/common/AirportSelector";
import api from "../../../../api/enhancedApi";

const { Text } = Typography;

const formatBH = (minutes) => {
    if (minutes == null) return "—";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h${String(m).padStart(2, "0")}m`;
};

const formatEntryMonth = (entryMonth) => {
    const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(entryMonth || "");
    return match ? `${match[1]}年${Number(match[2])}月` : "—";
};

const formatWindMonths = (months) => {
    if (!months || months.length === 0) return "—";
    return [...months].sort((a, b) => a - b).join(",");
};

/** 各段非空几月风的共同交集；全空则 null（展示为 —） */
const getSharedWindMonths = (legs) => {
    const filled = (legs || [])
        .map((leg) => leg.wind_months)
        .filter((months) => Array.isArray(months) && months.length > 0)
        .map((months) => months.map(Number));
    if (filled.length === 0) return null;

    let common = new Set(filled[0]);
    for (let index = 1; index < filled.length; index += 1) {
        common = new Set(filled[index].filter((month) => common.has(month)));
    }
    if (common.size === 0) return null;
    return [...common].sort((a, b) => a - b);
};

const directRouteToCombination = (route, origin, destination) => ({
    transit_airports: [],
    legs: [
        {
            ...route,
            departure_airport: origin,
            arrival_airport: destination,
        },
    ],
    total_block_time_minutes: route.block_time_minutes,
    total_block_fuel: route.block_fuel,
    entry_month: route.entry_month,
    wind_months: route.wind_months,
});

const ALL_AIRCRAFT_TYPE = "所有机型";

const getAircraftTypes = (source) => {
    if (source.aircraft_types?.length > 0) return source.aircraft_types;
    if (source.aircraft_type_hint) return [source.aircraft_type_hint];
    return [ALL_AIRCRAFT_TYPE];
};

const buildDisplayGroups = (result) => {
    if (!result) return [];

    const groupsByType = new Map();
    const addCard = (aircraftTypes, card) => {
        aircraftTypes.forEach((aircraftType) => {
            if (!groupsByType.has(aircraftType)) {
                groupsByType.set(aircraftType, { aircraftType, cards: [] });
            }
            groupsByType.get(aircraftType).cards.push(card);
        });
    };

    result.groups.forEach((group) => {
        group.combinations
            .filter((combo) => combo.legs.length <= 2)
            .forEach((combo) => {
                addCard(getAircraftTypes(group), {
                    key: combo.legs.map((leg) => leg.id).join("-"),
                    combo,
                    compatibleAircraft: group.aircraft,
                });
            });
    });

    result.direct_routes.forEach((route) => {
        addCard(getAircraftTypes(route), {
            key: `direct-${route.id}`,
            combo: directRouteToCombination(route, result.origin, result.destination),
            compatibleAircraft: route.compatible_aircraft || [],
        });
    });

    groupsByType.forEach((group) => {
        group.cards.sort((left, right) => (
            left.combo.legs.length - right.combo.legs.length
            || (left.combo.total_block_time_minutes ?? Number.MAX_SAFE_INTEGER)
                - (right.combo.total_block_time_minutes ?? Number.MAX_SAFE_INTEGER)
            || (left.combo.total_block_fuel ?? Number.MAX_SAFE_INTEGER)
                - (right.combo.total_block_fuel ?? Number.MAX_SAFE_INTEGER)
        ));
    });

    return Array.from(groupsByType.values()).sort((left, right) => {
        if (left.aircraftType === ALL_AIRCRAFT_TYPE) return 1;
        if (right.aircraftType === ALL_AIRCRAFT_TYPE) return -1;
        return left.aircraftType.localeCompare(right.aircraftType);
    });
};

const RouteChain = ({ legs }) => {
    const stations = [legs[0].departure_airport];
    legs.forEach((leg) => stations.push(leg.arrival_airport));

    return (
        <div className="rc-route-chain">
            {stations.map((code, i) => {
                const isOrigin = i === 0;
                const isDest = i === stations.length - 1;
                const dotClass = isOrigin ? "origin" : isDest ? "dest" : "transit";

                return (
                    <React.Fragment key={i}>
                        <div className="rc-station">
                            <div className={`rc-station-dot ${dotClass}`} />
                            <div className="rc-station-code">{code}</div>
                        </div>
                        {i < legs.length && (
                            <div className="rc-leg-segment">
                                <div className="rc-leg-name" title={legs[i].route_name}>
                                    {legs[i].route_name}
                                </div>
                                <div className="rc-leg-bar" />
                                <div className="rc-leg-bh">{formatBH(legs[i].block_time_minutes)}</div>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

const CombinationCard = ({ combo, compatibleAircraft }) => {
    const [expanded, setExpanded] = useState(false);
    const toggleExpanded = () => setExpanded((value) => !value);
    const sharedWindMonths = getSharedWindMonths(combo.legs);

    return (
        <Card
            size="small"
            hoverable
            className={`rc-combo-card ${expanded ? "expanded" : ""}`}
            onClick={toggleExpanded}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleExpanded();
                }
            }}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
        >
            <RouteChain legs={combo.legs} />
            <div className="rc-combo-stats">
                <div className="rc-stat">
                    <span className="rc-stat-label">总BH</span>
                    <span className="rc-stat-value highlight">{formatBH(combo.total_block_time_minutes)}</span>
                </div>
                <div className="rc-stat rc-month-stat">
                    <span className="rc-stat-label">几月风</span>
                    <Tag color="cyan" className="rc-wind-month-tag">{formatWindMonths(sharedWindMonths)}</Tag>
                </div>
                <div className="rc-stat rc-month-stat">
                    <span className="rc-stat-label">录入月份</span>
                    <Tag color="gold" className="rc-month-tag">{formatEntryMonth(combo.entry_month)}</Tag>
                </div>
                <div className="rc-stat">
                    <span className="rc-stat-label">燃油</span>
                    <span className="rc-stat-value">
                        {combo.total_block_fuel != null ? `${combo.total_block_fuel}T` : "—"}
                    </span>
                </div>
                <div className="rc-stat">
                    <span className="rc-stat-label">段数</span>
                    <span className="rc-stat-value">{combo.legs.length}段</span>
                </div>
                <span className="rc-expand-hint">{expanded ? "▲ 收起" : "▼ 展开明细"}</span>
            </div>

            {expanded && (
                <div className="rc-combo-detail">
                    <table className="rc-detail-table">
                        <thead>
                            <tr>
                                <th>段</th>
                                <th>航路名称</th>
                                <th>航段</th>
                                <th>BH</th>
                                <th>几月风</th>
                                <th>录入月份</th>
                                <th>NM</th>
                                <th>燃油(T)</th>
                                <th>Trip Fuel</th>
                                <th>FOD</th>
                            </tr>
                        </thead>
                        <tbody>
                            {combo.legs.map((leg, idx) => (
                                <tr key={leg.id}>
                                    <td>{idx + 1}</td>
                                    <td className="rc-td-name">{leg.route_name}</td>
                                    <td>{leg.departure_airport} → {leg.arrival_airport}</td>
                                    <td className="rc-td-bh">{formatBH(leg.block_time_minutes)}</td>
                                    <td><Tag color="cyan">{formatWindMonths(leg.wind_months)}</Tag></td>
                                    <td><Tag color="gold">{formatEntryMonth(leg.entry_month)}</Tag></td>
                                    <td className="rc-td-nm">{leg.nautical_miles ?? "—"}</td>
                                    <td className="rc-td-fuel">{leg.block_fuel != null ? `${leg.block_fuel}T` : "—"}</td>
                                    <td>{leg.trip_fuel != null ? `${Number(leg.trip_fuel).toLocaleString()}kg` : "—"}</td>
                                    <td>{leg.fuel_over_destination != null ? `${Number(leg.fuel_over_destination).toLocaleString()}kg` : "—"}</td>
                                </tr>
                            ))}
                            <tr className="rc-detail-row-total">
                                <td />
                                <td>合计</td>
                                <td>
                                    {combo.legs[0].departure_airport}
                                    {combo.transit_airports.map((t) => ` → ${t}`).join("")}
                                    {` → ${combo.legs[combo.legs.length - 1].arrival_airport}`}
                                </td>
                                <td>{formatBH(combo.total_block_time_minutes)}</td>
                                <td>{formatWindMonths(sharedWindMonths)}</td>
                                <td>{formatEntryMonth(combo.entry_month)}</td>
                                <td>—</td>
                                <td>{combo.total_block_fuel != null ? `${combo.total_block_fuel}T` : "—"}</td>
                                <td>—</td>
                                <td>—</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="rc-detail-extra">
                        {(() => {
                            const countries = [...new Set(combo.legs.flatMap((l) => l.overflight_countries || []))];
                            return countries.length > 0 ? (
                                <div className="rc-detail-extra-item">
                                    <span className="label">过境国家/地区:</span>
                                    {countries.map((c) => (
                                        <span key={c} className="rc-overflight-tag">{c}</span>
                                    ))}
                                </div>
                            ) : null;
                        })()}
                        {(() => {
                            return compatibleAircraft.length > 0 ? (
                                <div className="rc-detail-extra-item">
                                    <span className="label">适配飞机:</span>
                                    <span className="value">{compatibleAircraft.join(", ")}</span>
                                </div>
                            ) : (
                                <div className="rc-detail-extra-item">
                                    <span className="label">适配飞机:</span>
                                    <span className="value">所有飞机</span>
                                </div>
                            );
                        })()}
                        {combo.legs.some((l) => l.alternate_airports?.length > 0) && (
                            <div className="rc-detail-extra-item">
                                <span className="label">备降机场:</span>
                                <span className="value">
                                    {combo.legs
                                        .map((l, i) =>
                                            l.alternate_airports?.length > 0
                                                ? `${l.alternate_airports.join("/")} (第${i + 1}段)`
                                                : null,
                                        )
                                        .filter(Boolean)
                                        .join(", ")}
                                </span>
                            </div>
                        )}
                        {combo.legs.some((l) => l.block_fuel == null) && (
                            <div className="rc-detail-extra-item">
                                <span className="value" style={{ color: "#fa8c16" }}>
                                    部分航段燃油数据缺失
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};

const RouteCombinationFinder = () => {
    const [origin, setOrigin] = useState(undefined);
    const [destination, setDestination] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);

    const handleSearch = useCallback(async () => {
        if (!origin || !destination) {
            message.warning("请选择始发站和目的站");
            return;
        }
        if (origin === destination) {
            message.warning("始发站和目的站不能相同");
            return;
        }
        setLoading(true);
        setSearched(true);
        setError(null);
        try {
            const res = await api.flightRoute.getRouteCombinations(origin, destination);
            setResult(res.data);
        } catch (err) {
            console.error("组合查询失败:", err);
            setError("查询失败，请稍后重试");
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [origin, destination]);

    const handleSwap = () => {
        setOrigin(destination);
        setDestination(origin);
    };

    // 只按机型聚合；直飞与一次经停方案在同一机型分类中展示。
    const displayGroups = buildDisplayGroups(result);
    const totalCombos = displayGroups.reduce((sum, group) => sum + group.cards.length, 0);
    const hasResults = displayGroups.length > 0;

    return (
        <div className="rc-finder">
            {/* 搜索栏 */}
            <div className="rc-search-bar">
                <span className="rc-search-label">始发站</span>
                <AirportSelector
                    value={origin}
                    onChange={setOrigin}
                    valueType="iata_code"
                    placeholder="选择始发站"
                    style={{ width: 160 }}
                    allowClear
                />
                <Button
                    type="text"
                    icon={<SwapOutlined />}
                    onClick={handleSwap}
                    style={{ color: "#bbb" }}
                />
                <span className="rc-search-label">目的站</span>
                <AirportSelector
                    value={destination}
                    onChange={setDestination}
                    valueType="iata_code"
                    placeholder="选择目的站"
                    style={{ width: 160 }}
                    allowClear
                />
                <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={loading}
                >
                    搜索组合
                </Button>
                {hasResults && (
                    <span className="rc-search-summary">
                        找到 <b>{displayGroups.length}</b> 个机型分类，共 <b>{totalCombos}</b> 种飞法
                    </span>
                )}
            </div>

            {/* 内容区 */}
            {loading ? (
                <div className="rc-loading">
                    <Spin size="large" />
                    <p>正在计算航路组合…</p>
                </div>
            ) : !searched ? (
                <div className="rc-guide">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="请输入始发站和目的站，查看可用的航路组合"
                    />
                </div>
            ) : error ? (
                <div className="rc-empty">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span>
                                {error}
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    请检查网络连接或联系管理员
                                </Text>
                            </span>
                        }
                    />
                </div>
            ) : !hasResults ? (
                <div className="rc-empty">
                    <Empty
                        description={
                            <span>
                                未找到 <b>{result?.origin ?? origin}</b> → <b>{result?.destination ?? destination}</b> 的可用航路组合
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    可能原因：当前系统中没有从 {result?.origin ?? origin} 出发或到达 {result?.destination ?? destination} 的活跃航路，
                                    或各段航路的适配飞机无交集
                                </Text>
                            </span>
                        }
                    />
                </div>
            ) : (
                <div className="rc-results">
                    {/* 直飞与一次经停方案统一按机型分类 */}
                    {displayGroups.map((group) => (
                        <div key={group.aircraftType} className="rc-group-section">
                            <div className="rc-group-header">
                                <div className="rc-group-heading">
                                    <Tag
                                        color={group.aircraftType === ALL_AIRCRAFT_TYPE ? "default" : "blue"}
                                        className="rc-group-badge-type"
                                    >
                                        {group.aircraftType}
                                    </Tag>
                                    <Text strong>航路方案</Text>
                                </div>
                                <Tag color="processing" className="rc-group-count">{group.cards.length} 种飞法</Tag>
                            </div>
                            <div className="rc-combo-cards">
                                {group.cards.map((card) => (
                                    <CombinationCard
                                        key={`${group.aircraftType}-${card.key}`}
                                        combo={card.combo}
                                        compatibleAircraft={card.compatibleAircraft}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default RouteCombinationFinder;
